import { useEffect, useMemo, useRef, useState } from 'react';

export type SupportPanelTab = 'demandas' | 'sop' | 'historico';
export type TabBadge = 'none' | 'info' | 'alert';

const CRITICAL_LOG_ACTIONS = new Set([
  'reject',
  'return',
  'escalate',
  'handoff',
  'problem',
  'register_problem',
  'devolver',
]);

interface TaskLogLike {
  action?: string;
}

function isCriticalLog(log: TaskLogLike): boolean {
  const action = (log.action ?? '').toLowerCase();
  return [...CRITICAL_LOG_ACTIONS].some((key) => action.includes(key));
}

export interface UnifiedPanelBadges {
  demandas: TabBadge;
  sop: TabBadge;
  historico: TabBadge;
}

interface UseUnifiedPanelBadgesOptions {
  panelOpen: boolean;
  activeTab: SupportPanelTab;
  taskId: string | null;
  queueCount: number;
  overdueCount: number;
  logs: TaskLogLike[];
  taskRevision?: string | number | null;
  hasInstruction?: boolean;
}

export function useUnifiedPanelBadges({
  panelOpen,
  activeTab,
  taskId,
  queueCount,
  overdueCount,
  logs,
  taskRevision,
  hasInstruction = false,
}: UseUnifiedPanelBadgesOptions): UnifiedPanelBadges {
  const [seenQueueCount, setSeenQueueCount] = useState(queueCount);
  const [seenOverdueCount, setSeenOverdueCount] = useState(overdueCount);
  const [seenLogCount, setSeenLogCount] = useState(0);
  const [seenTaskRevision, setSeenTaskRevision] = useState<string | number | null>(null);
  const [seenInstruction, setSeenInstruction] = useState(false);
  const prevTaskIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (prevTaskIdRef.current !== taskId) {
      prevTaskIdRef.current = taskId;
      setSeenLogCount(logs.length);
      setSeenTaskRevision(taskRevision ?? null);
      setSeenQueueCount(queueCount);
      setSeenOverdueCount(overdueCount);
      setSeenInstruction(false);
    }
  }, [taskId, logs.length, taskRevision, queueCount, overdueCount]);

  useEffect(() => {
    if (!panelOpen) return;
    if (activeTab === 'demandas') {
      setSeenQueueCount(queueCount);
      setSeenOverdueCount(overdueCount);
    }
    if (activeTab === 'historico') {
      setSeenLogCount(logs.length);
    }
    if (activeTab === 'sop' && hasInstruction) {
      setSeenInstruction(true);
    }
  }, [panelOpen, activeTab, queueCount, overdueCount, logs.length, taskRevision, hasInstruction]);

  return useMemo(() => {
    const demandasUnread =
      (!panelOpen || activeTab !== 'demandas')
      && (overdueCount > seenOverdueCount || queueCount > seenQueueCount);

    const sopUnread =
      taskId != null
      && hasInstruction
      && (!panelOpen || activeTab !== 'sop')
      && !seenInstruction;

    const unreadLogs = logs.slice(0, Math.max(0, logs.length - seenLogCount));
    const historicoUnread =
      taskId != null
      && (!panelOpen || activeTab !== 'historico')
      && unreadLogs.length > 0;

    return {
      demandas: !demandasUnread
        ? 'none'
        : overdueCount > seenOverdueCount
          ? 'alert'
          : 'info',
      sop: sopUnread ? 'info' : 'none',
      historico: !historicoUnread
        ? 'none'
        : unreadLogs.some(isCriticalLog)
          ? 'alert'
          : 'info',
    } satisfies UnifiedPanelBadges;
  }, [
    panelOpen,
    activeTab,
    taskId,
    queueCount,
    overdueCount,
    seenQueueCount,
    seenOverdueCount,
    logs,
    seenLogCount,
    taskRevision,
    seenTaskRevision,
    hasInstruction,
    seenInstruction,
  ]);
}
